import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
} from 'react';
import { CarouselButton as AriaCarouselButton } from 'react-aria-carousel';
import { useFocusRing } from '@react-aria/focus';
import styles from './CarouselButton.module.css';
import iconButtonStyles from '../../IconButton/IconButton.module.css';
import { focusStyleVariants } from '../../../utils/focus/focusStyles';
import { Icon } from '../../Icon/Icon';

type CarouselButtonSize = 'sm' | 'md' | 'lg';

export interface CarouselButtonProps {
  /**
   * Direction of the button (prev or next)
   */
  dir: 'prev' | 'next';

  /**
   * Button style variant
   * @default 'neutral'
   */
  variant?: 'neutral' | 'white' | 'shapeFlat' | 'shapeElevated' | 'gradient';

  /**
   * Button size
   * @default 'md'
   */
  size?: CarouselButtonSize;

  /**
   * Focus style
   * @default 'default'
   */
  focusStyle?: 'default' | 'white';

  /**
   * Whether to hide the button when disabled
   * @default false
   */
  hideDisabledArrow?: boolean;

  /**
   * Whether the button is disabled (for standalone use)
   * @default false
   */
  isDisabled?: boolean;

  /**
   * Override the hideDisabledArrow state when itemsPerPage > 1 and looping is off
   * eg, when itemsPerPage=3, scrolling 4/5 into view, enables the prev arrow to be enabled/made visible
   * @private Internal use only
   */
  overrideHideDisabledArrow?: boolean;

  /**
   * Additional CSS class name for the button container
   */
  className?: string;

  /**
   * Additional CSS class name for the button content
   */
  buttonClassName?: string;

  /**
   * Custom click handler for implementing custom navigation behavior
   */
  onClick?: () => void;

  /**
   * Callback fired when the button is pressed
   */
  onPress?: () => void;

  /**
   * Whether to use standalone mode (outside carousel context)
   * @default false
   */
  standalone?: boolean;

  // Internal prop to manage the icon type
  /**
   * @private
   */
  iconType?: 'chevron';
}

const isActivationKey = (key: string) => key === ' ' || key === 'Enter';

const getIconSizeForButton = (size: CarouselButtonSize) => {
  if (size === 'sm') return '0.75x';
  if (size === 'lg') return '2x';
  if (size === 'md') return '1.25x';
  return '1x';
};

const getIconColorForVariant = (
  variant: NonNullable<CarouselButtonProps['variant']>,
) => {
  switch (variant) {
    case 'white':
      return 'whitePrimary';
    case 'gradient':
      return 'accentPrimary';
    default:
      return 'whiteOnVibrant';
  }
};

const getVariantStyleClass = (
  variant: NonNullable<CarouselButtonProps['variant']>,
) => styles[variant] || '';

const getContainerFocusClass = (
  shouldBeFullHeight: boolean,
  shouldShowFocusRing: boolean,
  focusStyle: NonNullable<CarouselButtonProps['focusStyle']>,
) =>
  shouldBeFullHeight && shouldShowFocusRing
    ? focusStyleVariants({ focusStyle })
    : '';

const getInnerFocusClass = (
  shouldBeFullHeight: boolean,
  shouldShowFocusRing: boolean,
  focusStyle: NonNullable<CarouselButtonProps['focusStyle']>,
) =>
  !shouldBeFullHeight && shouldShowFocusRing
    ? focusStyleVariants({ focusStyle })
    : '';

const getButtonClasses = ({
  size,
  variant,
  shouldBeFullHeight,
  shouldShowFocusRing,
  focusStyle,
  buttonClassName,
}: {
  size: CarouselButtonSize;
  variant: NonNullable<CarouselButtonProps['variant']>;
  shouldBeFullHeight: boolean;
  shouldShowFocusRing: boolean;
  focusStyle: NonNullable<CarouselButtonProps['focusStyle']>;
  buttonClassName: string;
}) => {
  const styleVariant = variant === 'white' ? 'white' : 'neutral';
  const emphasis = variant === 'white' ? 'secondary' : 'quaternary';

  const baseClass = iconButtonStyles.iconButton;
  const sizeClass = iconButtonStyles[size] || '';
  const emphasisClassKey = `${styleVariant}${emphasis.charAt(0).toUpperCase() + emphasis.slice(1)}`;
  const emphasisClass = iconButtonStyles[emphasisClassKey] || '';
  const focusClass = getInnerFocusClass(
    shouldBeFullHeight,
    shouldShowFocusRing,
    focusStyle,
  );

  return `${baseClass} ${sizeClass} ${emphasisClass} ${focusClass} ${buttonClassName || ''}`;
};

const getTabIndexForButton = (
  standalone: boolean,
  hideDisabledArrow: boolean,
  effectiveIsDisabled: boolean,
) => {
  if (standalone) {
    return hideDisabledArrow && effectiveIsDisabled ? -1 : 0;
  }

  return effectiveIsDisabled ? -1 : 0;
};

const getAriaLabelForButton = (
  dir: 'prev' | 'next',
  standalone: boolean,
  effectiveIsDisabled: boolean,
) => {
  const action = dir === 'prev' ? 'Previous' : 'Next';
  const context = standalone ? 'item' : 'slide';
  const state = effectiveIsDisabled ? ', unavailable' : '';
  return `${action} ${context}${state}`;
};

const assignForwardedRef = (
  element: HTMLButtonElement | null,
  localRef: React.MutableRefObject<HTMLButtonElement | null>,
  forwardedRef: React.ForwardedRef<HTMLButtonElement>,
) => {
  localRef.current = element;
  if (!forwardedRef) return;
  if (typeof forwardedRef === 'function') {
    forwardedRef(element);
    return;
  }
  forwardedRef.current = element;
};

const triggerButtonClick = (
  effectiveIsDisabled: boolean,
  onClick?: () => void,
  onPress?: () => void,
) => {
  if (!effectiveIsDisabled) {
    onClick?.();
    onPress?.();
  }
};

const carouselButtonKeyDown = (
  e: React.KeyboardEvent,
  standalone: boolean,
  effectiveIsDisabled: boolean,
) => {
  if (standalone && effectiveIsDisabled) return;
  if (isActivationKey(e.key)) e.preventDefault();
};

const carouselButtonKeyUp = (
  e: React.KeyboardEvent,
  standalone: boolean,
  effectiveIsDisabled: boolean,
  triggerClick: () => void,
  buttonRef: React.RefObject<HTMLButtonElement | null>,
) => {
  if (standalone && effectiveIsDisabled) return;
  if (!isActivationKey(e.key) || effectiveIsDisabled) return;
  e.preventDefault();
  if (standalone) {
    triggerClick();
    return;
  }
  buttonRef.current?.click();
};

const processAriaDisabledMutation = (
  mutation: MutationRecord,
  element: HTMLElement,
  setCarouselIsDisabled: (v: boolean) => void,
  resetInteractionState: () => void,
) => {
  if (
    mutation.type !== 'attributes' ||
    mutation.attributeName !== 'aria-disabled'
  ) {
    return;
  }
  const isDisabled = element.getAttribute('aria-disabled') === 'true';
  setCarouselIsDisabled(isDisabled);
  if (isDisabled) resetInteractionState();
};

const notifySiblingButtonsToHideFocus = (element: HTMLElement) => {
  const carouselContainer = element.closest(
    '[role="region"][aria-roledescription="carousel"], .carouselWrapper',
  );
  if (!carouselContainer) return;
  carouselContainer
    .querySelectorAll('[dir="prev"], [dir="next"]')
    .forEach((button) => {
      if (button !== element && button instanceof HTMLElement) {
        button.dispatchEvent(new CustomEvent('carousel:hideFocus'));
      }
    });
};

const handleButtonFocusEvent = (
  e: FocusEvent,
  element: HTMLElement,
  isFocusVisible: boolean,
  standalone: boolean,
  setShouldShowFocusRing: (v: boolean) => void,
) => {
  if (e.target !== element || !isFocusVisible) return;
  setShouldShowFocusRing(true);
  if (!standalone) {
    notifySiblingButtonsToHideFocus(element);
  }
};

export const CarouselButton = forwardRef<
  HTMLButtonElement,
  CarouselButtonProps
>(function CarouselButton(props, forwardedRef) {
  const {
    dir,
    variant = 'neutral',
    size = 'md',
    focusStyle = 'default',
    hideDisabledArrow = false,
    isDisabled = false,
    overrideHideDisabledArrow,
    iconType = 'chevron',
    className = '',
    buttonClassName = '',
    onClick,
    onPress,
    standalone = false,
  } = props;
  const { isFocusVisible, focusProps, isFocused } = useFocusRing();
  const directionClass = dir === 'prev' ? styles.prev : styles.next;
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [carouselIsDisabled, setCarouselIsDisabled] = useState(false);
  const [shouldShowFocusRing, setShouldShowFocusRing] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const setButtonRef = useCallback(
    (element: HTMLButtonElement | null) =>
      assignForwardedRef(element, buttonRef, forwardedRef),
    [forwardedRef],
  );

  const variantStyleClass = getVariantStyleClass(variant);

  // Determine if this button should be full height based on gradient variant
  const shouldBeFullHeight = variant === 'gradient';

  // For standalone mode, use the isDisabled prop directly
  // For carousel mode, track aria-disabled attribute changes
  const effectiveIsDisabled = standalone
    ? isDisabled
    : (overrideHideDisabledArrow ?? carouselIsDisabled);

  // Set up the MutationObserver to track disabled state changes (carousel mode only)
  useEffect(() => {
    if (standalone) return;

    const element = buttonRef.current;
    if (!element) return;

    // Set initial disabled state
    setCarouselIsDisabled(element.getAttribute('aria-disabled') === 'true');

    // Use MutationObserver to track changes to aria-disabled
    const resetInteractionState = () => {
      setIsHovered(false);
      setIsPressed(false);
      setShouldShowFocusRing(false);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) =>
        processAriaDisabledMutation(
          mutation,
          element,
          setCarouselIsDisabled,
          resetInteractionState,
        ),
      );
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: ['aria-disabled'],
    });

    // Clean up the observer on unmount
    return () => {
      observer.disconnect();
    };
  }, [standalone]);

  // Handle focus coordination with proper cleanup
  useEffect(() => {
    if (!buttonRef.current) return;

    const element = buttonRef.current;

    const handleFocus = (e: FocusEvent) =>
      handleButtonFocusEvent(
        e,
        element,
        isFocusVisible,
        standalone,
        setShouldShowFocusRing,
      );
    const handleBlur = () => setShouldShowFocusRing(false);
    const handleHideFocus = () => setShouldShowFocusRing(false);

    element.addEventListener('focus', handleFocus);
    element.addEventListener('blur', handleBlur);
    element.addEventListener('carousel:hideFocus', handleHideFocus);

    return () => {
      element.removeEventListener('focus', handleFocus);
      element.removeEventListener('blur', handleBlur);
      element.removeEventListener('carousel:hideFocus', handleHideFocus);
    };
  }, [isFocusVisible, standalone]);

  // Update focus ring state when isFocusVisible changes
  useEffect(() => {
    if (!isFocusVisible) {
      setShouldShowFocusRing(false);
    } else if (isFocused && isFocusVisible) {
      setShouldShowFocusRing(true);
    }
  }, [isFocusVisible, isFocused]);

  const containerFocusClass = getContainerFocusClass(
    shouldBeFullHeight,
    shouldShowFocusRing,
    focusStyle,
  );

  // Only apply hover/press handlers if not disabled
  const mouseEventHandlers = effectiveIsDisabled
    ? {}
    : {
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        onMouseDown: () => setIsPressed(true),
        onMouseUp: () => setIsPressed(false),
      };

  const handleClick = useCallback(
    () => triggerButtonClick(effectiveIsDisabled, onClick, onPress),
    [effectiveIsDisabled, onClick, onPress],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) =>
      carouselButtonKeyDown(e, standalone, effectiveIsDisabled),
    [standalone, effectiveIsDisabled],
  );

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent) =>
      carouselButtonKeyUp(
        e,
        standalone,
        effectiveIsDisabled,
        handleClick,
        buttonRef,
      ),
    [standalone, effectiveIsDisabled, handleClick],
  );

  // Common button props
  const commonProps = {
    className: `${styles.arrowContainer} ${directionClass} ${hideDisabledArrow ? styles.hideDisabledArrow : ''} ${variantStyleClass || ''} ${shouldBeFullHeight ? styles.fullHeight : ''} ${containerFocusClass} ${className || ''}`,
    ...focusProps,
    ...mouseEventHandlers,
    'data-hovered': !effectiveIsDisabled && isHovered ? true : undefined,
    'data-pressed': !effectiveIsDisabled && isPressed ? true : undefined,
    'data-disabled': effectiveIsDisabled ? true : undefined,
    'data-focused': !effectiveIsDisabled && isFocused ? true : undefined,
    'data-focus-visible': shouldShowFocusRing ? true : undefined,
    ref: setButtonRef,
    tabIndex: getTabIndexForButton(
      standalone,
      hideDisabledArrow,
      effectiveIsDisabled,
    ),
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    'aria-label': getAriaLabelForButton(dir, standalone, effectiveIsDisabled),
    'aria-disabled':
      !standalone && overrideHideDisabledArrow !== undefined
        ? overrideHideDisabledArrow
          ? true
          : undefined
        : undefined,
    role: 'button',
  };

  // Button content
  const buttonContent = (
    <div
      className={`${getButtonClasses({
        size,
        variant,
        shouldBeFullHeight,
        shouldShowFocusRing,
        focusStyle,
        buttonClassName,
      })} ${styles.iconButton}`}
      data-focused={
        !effectiveIsDisabled && isFocused && shouldShowFocusRing
          ? true
          : undefined
      }
      data-focus-visible={
        !effectiveIsDisabled && shouldShowFocusRing ? true : undefined
      }
      data-parent-hovered={!effectiveIsDisabled && isHovered ? true : undefined}
      data-parent-pressed={!effectiveIsDisabled && isPressed ? true : undefined}
      // For standalone mode, don't show as disabled
      data-parent-disabled={
        standalone ? undefined : effectiveIsDisabled ? true : undefined
      }
      aria-hidden="true"
    >
      <Icon
        iconName={`${iconType}-${dir === 'prev' ? 'left' : 'right'}`}
        color={getIconColorForVariant(variant)}
        iconSize={getIconSizeForButton(size)}
        aria-hidden="true"
      />
    </div>
  );

  // Render standalone button or carousel button based on mode
  if (standalone) {
    return (
      <button
        {...commonProps}
        onClick={handleClick}
        // Don't set aria-disabled for standalone buttons since we want them always enabled
      >
        {buttonContent}
      </button>
    );
  }

  // Default carousel mode
  return (
    <AriaCarouselButton dir={dir} {...commonProps} onClick={handleClick}>
      {buttonContent}
    </AriaCarouselButton>
  );
});

CarouselButton.displayName = 'CarouselButton';

export default CarouselButton;
